import React, { useEffect, useState } from 'react'
import { _FORM_ITEM_LAYOUT, _MINERALS, _TAIL_FORM_ITEM_LAYOUT } from '../../../../constants';
import { Label, MineralsFormContainer, MineralsWrapper, StyledForm, StyledFormUpload } from './style';
import { App, Button, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select, Upload, UploadProps } from 'antd';
import UserLookup from '../../../Common/Form/UserLookup';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useCreateHMA } from '../../../../hooks/admin/useHma';
import { useAdminSignInAsProfessional } from '../../../../hooks/admin/useAdminProfessional';
import { useToggleProfessionalViewable } from '../../../../hooks/admin/useListAnalysesView';
import { useToggleClientViewable } from '../../../../hooks/professionals/useAnalyses';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { getNumericId } from '../../../../helpers';

interface HMAFormProps {
  onReset: boolean;
  onSuccess?: () => void;
  initialProfessionalId?: string | number;
  initialClientId?: string;
}

const { Option } = Select;

const HMAForm: React.FC<HMAFormProps> = ({ onReset, onSuccess, initialProfessionalId, initialClientId }) => {
  const { create: createHMA } = useCreateHMA();
  const { toggle } = useToggleProfessionalViewable();
  const { toggleClientViewable } = useToggleClientViewable();
  const token = useSelector((state: any) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const professionals = useSelector((state: any) => state.professionals.professionals || []);
  const { signInAsProfessional } = useAdminSignInAsProfessional();
  const [form] = StyledForm.useForm();
  const { message } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileObj, setFileObj] = useState<any>(null);
  const submitLock = React.useRef(false);

  useEffect(() => {
    if (onReset) {
      form.resetFields();
    }
  }, [onReset, form]);

  const onFinish = async (values: any) => {
    console.log('HMAForm - onFinish triggered');
    if (submitLock.current) {
      console.warn('HMAForm - Double submission blocked by lock');
      return;
    }
    submitLock.current = true;
    console.log('HMAForm - Submission lock acquired, starting upload...');
    setIsSubmitting(true);
    const { professional, client, attachmentFile, reportNumber, dateOfAnalysis, hairType, ...hmaData } = values;

    const parsedHmaData: { [key: string]: number | string | null } = {};
    for (const key in hmaData) {
      if (hmaData.hasOwnProperty(key)) {
        const value = hmaData[key];
        parsedHmaData[key] = (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) ? parseFloat(value) : value;
      }
    }

    const parsedClientId = getNumericId(client);
    const parsedProfessionalId = getNumericId(professional);

    try {
      const res = await createHMA({
        professionalId: parsedProfessionalId,
        hmaData: {
          clientId: parsedClientId,
          reportNumber,
          attachment: fileObj,
          dateOfAnalysis: dateOfAnalysis ? dateOfAnalysis.format('YYYY-MM-DD') : undefined,
          hairType,
          ...parsedHmaData
        }
      });

      if (res?.inputHma?.id) {
        const analysesId = res.inputHma.id; // Use Global ID (GID) for toggles as per professional dashboard fix
        const professionalId = getNumericId(parsedProfessionalId);
        const clientId = getNumericId(parsedClientId);

        // Toggle Professional Viewable (Admin action with Admin token)
        await toggle({
          analysesId,
          analysesType: 'hma',
          professionalId
        });

        const isAdmin = user?.__typename === 'Admin' || !(user as any)?.code;

        if (isAdmin) {
          const selectedProf = professionals.find((p: any) => getNumericId(p.id) === professionalId);
          if (selectedProf?.email) {
            try {
              const proSignIn = await signInAsProfessional(selectedProf.email);
              if (proSignIn?.token) {
                // Toggle Client Viewable using the same hook/approach that worked elsewhere
                await toggleClientViewable(clientId.toString(), analysesId, 'hma', proSignIn.token);
              }
            } catch (ghostErr) {
              console.warn('Ghost login failed for toggleClient:', ghostErr);
            }
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('HMAForm - Upload successful');
      message.success('HMA data saved successfully');
      form.resetFields();

      if (onSuccess) {
        await onSuccess();
      }
    } catch (err: any) {
      console.error('onSubmitHMA Error', err);
      message.error(err.message || 'Failed to save HMA data');
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: (file) => {
      setFileObj(file);
      form.setFieldsValue({ attachmentFile: file });
      return false;
    },
    onRemove: () => {
      setFileObj(null);
      form.setFieldsValue({ attachmentFile: undefined });
    }
  };

  return (
    <StyledForm
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <Row gutter={24}>
        <Col lg={12}>
          <UserLookup 
            form={form} 
            selectedProfessional={initialProfessionalId as number} 
            selectedClient={initialClientId} 
          />
        </Col>
        <Col lg={12}>
          <Form.Item
            name="reportNumber"
            label="Report Number"
            rules={[{ required: true, message: 'Report Number is required' }]}
          >
            <Input placeholder="Enter report number" />
          </Form.Item>
          <Form.Item
            name="dateOfAnalysis"
            label="Date of Analysis"
            rules={[{ required: true, message: 'Date of Analysis is required' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="hairType"
            label="Hair Type"
            rules={[{ required: true, message: 'Hair Type is required' }]}
          >
            <Select placeholder="Select hair type">
              <Option value="Head">Head</Option>
              <Option value="Pubic">Pubic</Option>
              <Option value="Head and pubic">Head and pubic</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider style={{ margin: '1rem 0' }} />
      
      <Form.Item
        name="attachmentFile"
        label="Attachment"
        rules={[{ required: true, message: 'Attachment is required' }]}
      >
        <div>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </div>
      </Form.Item>

      <Divider orientation="left">Minerals</Divider>
      <MineralsWrapper>
        <MineralsFormContainer>
          {_MINERALS.map((mineral) => (
            <div key={mineral.name}>
              <Label>{mineral.label}</Label>
              <Form.Item
                name={mineral.name}
                rules={[
                  {
                    required: true,
                    message: `${mineral.label} can't be blank`,
                  },
                  {
                    type: 'number',
                    min: 0.0001,
                    message: `${mineral.label} must be greater than 0`,
                  },
                ]}
              >
                <InputNumber min={0.0001} style={{ width: '100%' }} placeholder={`Enter ${mineral.label}`} />
              </Form.Item>
            </div>
          ))}
        </MineralsFormContainer>
      </MineralsWrapper>
      <StyledForm.Item>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm>
  )
}

export default HMAForm;